import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { FaCheckCircle } from "react-icons/fa";
import { getClassesBySchoolId, getStudentsByClassId, getTeachersBySchoolId, mergeClasses } from "../services/TeacherAPIClient";
import { getDistinctClassGradeLabel } from "../utils/classDisplay";
import "./TeacherMergeClassesModal.css";

const DUPLICATE_DECISIONS = [
  { value: "merge_records", label: "Same child - combine records", detail: "Bring results together for this child" },
  { value: "keep_separate", label: "Different child - keep separate", detail: "Keep both records separate" },
  { value: "needs_review", label: "I need help matching these", detail: "Send this merge for support review" }
];

const SOURCE_CLASS_DISPOSITIONS = [
  {
    value: "archive",
    label: "Remove emptied class from my class list",
    detail: (selectedFromClassName) => `${selectedFromClassName} will no longer appear once its children have moved.`
  },
  {
    value: "keep_empty",
    label: "Keep emptied class visible",
    detail: () => "Use only if you still need to review the old class."
  }
];

const getClassId = (classItem) => classItem?.id || classItem?._id || classItem?.classid || classItem?.classId;

const normaliseText = (value) => String(value || "").trim().replace(/\s+/g, " ");

const getTeacherId = (teacher) => teacher?.id || teacher?._id || teacher?.teacherid || teacher?.teacherId;

const normaliseTeacher = (teacher) => ({
  id: getTeacherId(teacher),
  name: teacher?.name || teacher?.fullName || teacher?.displayName || "Unnamed teacher",
  detail: teacher?.email || teacher?.roleLabel || "Teacher"
});

const normaliseClass = (classItem, countryGrades) => {
  const studentCount =
    classItem?.studentNumber ??
    classItem?.studentCount ??
    classItem?.studentsCount ??
    classItem?.students?.length;
  const name = classItem?.name || classItem?.className || "Unnamed class";
  const year = classItem?.academicYear || classItem?.academic_year || classItem?.academicyear || "Current year";
  const grade = getDistinctClassGradeLabel(classItem, name, countryGrades);

  return {
    ...classItem,
    id: getClassId(classItem),
    name,
    detail: [
      grade,
      Number.isFinite(Number(studentCount)) ? `${Number(studentCount)} children` : "",
      year
    ].filter(Boolean).join(" - "),
    searchText: [name, grade, year].filter(Boolean).join(" ").toLowerCase()
  };
};

const getStudentName = (student) =>
  normaliseText(
    student?.name ||
    student?.fullName ||
    [student?.firstName || student?.firstname, student?.lastName || student?.lastname].filter(Boolean).join(" ")
  );

const getStudentId = (student) => student?.id || student?._id || student?.studentid || student?.studentId;

const normaliseStudent = (student) => ({
  ...student,
  id: getStudentId(student),
  name: getStudentName(student)
});

const createIdempotencyKey = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `merge-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getMergeErrorMessage = (submitError) =>
  submitError?.response?.data?.message ||
  submitError?.message ||
  "Class merge could not be completed. Refresh the class data and try again.";

function TeacherMergeClassesModal({
  show,
  handleClose,
  schoolid,
  countryGrades = null,
  teacherid,
  prefill = null,
  onReturnToTeacherDashboard = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [fromClassName, setFromClassName] = useState("");
  const [selectedFromClass, setSelectedFromClass] = useState(null);
  const [toClassName, setToClassName] = useState("");
  const [selectedToClass, setSelectedToClass] = useState(null);
  const [ownerTeacherSearch, setOwnerTeacherSearch] = useState("");
  const [selectedOwnerTeacher, setSelectedOwnerTeacher] = useState(null);
  const [emptiedClassAction, setEmptiedClassAction] = useState("");
  const [sourceOnlyChildrenCount, setSourceOnlyChildrenCount] = useState(0);
  const [duplicateChildren, setDuplicateChildren] = useState([]);
  const [duplicateDecisions, setDuplicateDecisions] = useState({});
  const [mergeResult, setMergeResult] = useState(null);
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);
  const [isLoadingDuplicates, setIsLoadingDuplicates] = useState(false);
  const [isSubmittingMerge, setIsSubmittingMerge] = useState(false);
  const [submitIdempotencyKey, setSubmitIdempotencyKey] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return;

    setStage("form");
    setClasses([]);
    setTeachers([]);
    setFromClassName(prefill?.fromClassName || prefill?.className || "");
    setSelectedFromClass(null);
    setToClassName(prefill?.toClassName || prefill?.newClassName || "");
    setSelectedToClass(null);
    setOwnerTeacherSearch(prefill?.teacherName || "");
    setSelectedOwnerTeacher(null);
    setEmptiedClassAction("");
    setSourceOnlyChildrenCount(0);
    setDuplicateChildren([]);
    setDuplicateDecisions({});
    setMergeResult(null);
    setIsSubmittingMerge(false);
    setSubmitIdempotencyKey("");
    setError("");

    if (!schoolid) {
      setError("Classes could not be loaded. Please try again.");
      return;
    }

    setIsLoadingClasses(true);
    Promise.all([getClassesBySchoolId(schoolid), getTeachersBySchoolId(schoolid)])
      .then(([classesResponse, teachersResponse]) => {
        const normalisedClasses = (Array.isArray(classesResponse) ? classesResponse : [])
          .map((classItem) => normaliseClass(classItem, countryGrades))
          .filter((classItem) => classItem.id && classItem.active !== false)
          .sort((firstClass, secondClass) => firstClass.name.localeCompare(secondClass.name));
        const normalisedTeachers = (Array.isArray(teachersResponse) ? teachersResponse : [])
          .map(normaliseTeacher)
          .filter((teacher) => teacher.id)
          .sort((firstTeacher, secondTeacher) => firstTeacher.name.localeCompare(secondTeacher.name));
        const prefilledFromClass = normalisedClasses.find(
          (classItem) => normaliseText(classItem.name).toLowerCase() === normaliseText(prefill?.fromClassName || prefill?.className || "").toLowerCase()
        );
        const prefilledToClass = normalisedClasses.find(
          (classItem) => normaliseText(classItem.name).toLowerCase() === normaliseText(prefill?.toClassName || prefill?.newClassName || "").toLowerCase()
        );
        const prefilledOwnerTeacher = normalisedTeachers.find(
          (teacher) => normaliseText(teacher.name).toLowerCase() === normaliseText(prefill?.teacherName || "").toLowerCase()
        );

        setClasses(normalisedClasses);
        setTeachers(normalisedTeachers);
        if (prefilledFromClass) {
          setSelectedFromClass(prefilledFromClass);
          setFromClassName(prefilledFromClass.name);
        }
        if (prefilledToClass) {
          setSelectedToClass(prefilledToClass);
          setToClassName(prefilledToClass.name);
        }
        if (prefilledOwnerTeacher) {
          setSelectedOwnerTeacher(prefilledOwnerTeacher);
          setOwnerTeacherSearch(prefilledOwnerTeacher.name);
        }
      })
      .catch((loadError) => {
        console.error("Error loading merge classes:", loadError);
        setError("Classes could not be loaded. Please try again.");
      })
      .finally(() => {
        setIsLoadingClasses(false);
      });
  }, [show, schoolid, countryGrades, prefill]);

  const normalisedFromClass = fromClassName.trim().toLowerCase();
  const normalisedToClass = toClassName.trim().toLowerCase();
  const sameClass = normalisedFromClass && normalisedFromClass === normalisedToClass;
  const canContinue = selectedFromClass && selectedToClass && fromClassName.trim() && toClassName.trim() && !sameClass;
  const hasNeedsReviewDecision = duplicateChildren.some(
    (duplicateChild) => duplicateDecisions[duplicateChild.sourceStudentId] === "needs_review"
  );
  const duplicateMatchingComplete =
    Boolean(emptiedClassAction) &&
    Boolean(selectedOwnerTeacher) &&
    duplicateChildren.every((duplicateChild) => Boolean(duplicateDecisions[duplicateChild.sourceStudentId])) &&
    !hasNeedsReviewDecision;
  const selectedFromClassName = fromClassName.trim() || "source class";
  const selectedToClassName = toClassName.trim() || "destination class";
  const filteredFromClasses = classes.filter((classItem) =>
    !fromClassName.trim() || classItem.searchText.includes(normaliseText(fromClassName).toLowerCase())
  );
  const filteredToClasses = classes.filter((classItem) =>
    (!toClassName.trim() || classItem.searchText.includes(normaliseText(toClassName).toLowerCase())) &&
    classItem.id !== selectedFromClass?.id
  );
  const filteredOwnerTeachers = teachers.filter((teacher) =>
    !ownerTeacherSearch.trim() ||
    [teacher.name, teacher.detail].some((value) =>
      normaliseText(value).toLowerCase().includes(normaliseText(ownerTeacherSearch).toLowerCase())
    )
  );

  const loadDuplicateChildren = async () => {
    if (!selectedFromClass || !selectedToClass || !schoolid) {
      setStage("review");
      return;
    }

    setIsLoadingDuplicates(true);
    setError("");

    try {
      const [fromStudents, toStudents] = await Promise.all([
        getStudentsByClassId(schoolid, selectedFromClass.id),
        getStudentsByClassId(schoolid, selectedToClass.id)
      ]);
      const normalisedToStudents = (Array.isArray(toStudents) ? toStudents : [])
        .map(normaliseStudent)
        .filter((student) => student.id && student.name);
      const normalisedFromStudents = (Array.isArray(fromStudents) ? fromStudents : [])
        .map(normaliseStudent)
        .filter((student) => student.id && student.name);
      const targetStudentsByName = normalisedToStudents.reduce((studentsByName, student) => {
        const nameKey = student.name.toLowerCase();
        studentsByName[nameKey] = [...(studentsByName[nameKey] || []), student];
        return studentsByName;
      }, {});
      const duplicates = normalisedFromStudents
        .map((sourceStudent) => {
          const targetStudents = targetStudentsByName[sourceStudent.name.toLowerCase()] || [];

          if (targetStudents.length < 1) {
            return null;
          }

          return {
            sourceStudentId: sourceStudent.id,
            targetStudentId: targetStudents[0].id,
            name: sourceStudent.name,
            targetStudents
          };
        })
        .filter(Boolean);

      setSourceOnlyChildrenCount(
        normalisedFromStudents.filter((student) => !targetStudentsByName[student.name.toLowerCase()]?.length).length
      );
      setDuplicateChildren(duplicates);
      setDuplicateDecisions({});
      setStage("review");
    } catch (loadError) {
      console.error("Error loading duplicate child review:", loadError);
      setSourceOnlyChildrenCount(0);
      setDuplicateChildren([]);
      setDuplicateDecisions({});
      setStage("review");
    } finally {
      setIsLoadingDuplicates(false);
    }
  };

  const handleSubmitMerge = async () => {
    if (!duplicateMatchingComplete || isSubmittingMerge) return;

    const idempotencyKey = submitIdempotencyKey || createIdempotencyKey();
    const duplicateChildDecisions = duplicateChildren.map((duplicateChild) => ({
      sourceStudentId: duplicateChild.sourceStudentId,
      targetStudentId: duplicateChild.targetStudentId,
      decision: duplicateDecisions[duplicateChild.sourceStudentId]
    }));

    setSubmitIdempotencyKey(idempotencyKey);
    setIsSubmittingMerge(true);
    setError("");

    try {
      const response = await mergeClasses(
        schoolid,
        {
          sourceClassId: selectedFromClass.id,
          targetClassId: selectedToClass.id,
          ownerTeacherId: selectedOwnerTeacher.id,
          sourceClassDisposition: emptiedClassAction,
          duplicateChildDecisions,
          requestedByTeacherId: teacherid
        },
        idempotencyKey
      );

      setMergeResult(response);
      setSubmitIdempotencyKey("");
      setStage("success");
    } catch (submitError) {
      console.error("Error merging classes:", submitError);
      setError(getMergeErrorMessage(submitError));
    } finally {
      setIsSubmittingMerge(false);
    }
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: mergeResult?.auditReference || mergeResult?.mergeId || selectedToClass?.id || selectedToClassName || "",
        message: "Classes merged."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderClassOption = (option, selected, onSelect) => (
    <button
      key={option.id || option.name}
      type="button"
      className={`merge-classes-result${selected ? " selected" : ""}`}
      onClick={() => onSelect(option)}
      aria-label={`${selected ? "Selected" : "Select"} ${option.name}`}
      aria-pressed={selected}
    >
      <span className="merge-classes-radio" aria-hidden="true" />
      <span>
        <strong>{option.name}</strong>
        <span>{option.detail}</span>
      </span>
    </button>
  );

  const renderClassResults = (items, selectedClass, selectClass, emptyCopy) => {
    if (isLoadingClasses) {
      return <div className="merge-classes-search-state"><Spinner animation="border" size="sm" /> <span>Loading classes...</span></div>;
    }

    if (selectedClass) {
      return renderClassOption(selectedClass, true, () => selectClass(null));
    }

    if (items.length < 1) {
      return <p className="merge-classes-muted">{emptyCopy}</p>;
    }

    return items.slice(0, 5).map((option) => renderClassOption(option, false, () => selectClass(option)));
  };

  const renderTeacherOption = (teacher, selected, onSelect) => (
    <button
      key={teacher.id}
      type="button"
      className={`merge-classes-result${selected ? " selected" : ""}`}
      onClick={() => onSelect(teacher)}
      aria-label={`${selected ? "Selected" : "Select"} ${teacher.name}`}
      aria-pressed={selected}
    >
      <span className="merge-classes-radio" aria-hidden="true" />
      <span>
        <strong>{teacher.name}</strong>
        <span>{teacher.detail}</span>
      </span>
    </button>
  );

  const renderOwnerTeacherResults = () => {
    if (isLoadingClasses) {
      return <div className="merge-classes-search-state"><Spinner animation="border" size="sm" /> <span>Loading teachers...</span></div>;
    }

    if (selectedOwnerTeacher) {
      return renderTeacherOption(selectedOwnerTeacher, true, () => setSelectedOwnerTeacher(null));
    }

    if (!ownerTeacherSearch.trim()) {
      return <p className="merge-classes-muted">Search for the teacher who should own the class.</p>;
    }

    if (filteredOwnerTeachers.length < 1) {
      return <p className="merge-classes-muted">No matching teachers found.</p>;
    }

    return filteredOwnerTeachers.slice(0, 5).map((teacher) =>
      renderTeacherOption(teacher, false, (selectedTeacher) => {
        setSelectedOwnerTeacher(selectedTeacher);
        setOwnerTeacherSearch(selectedTeacher.name);
      })
    );
  };

  const renderRadioCard = (option, selected, onSelect) => (
    <button
      key={option.value}
      type="button"
      className={`merge-classes-result${selected ? " selected" : ""}`}
      onClick={() => onSelect(option.value)}
    >
      <span className="merge-classes-radio" aria-hidden="true" />
      <span>
        <strong>{option.label || option.value}</strong>
        <span>{option.detail}</span>
      </span>
    </button>
  );

  return (
    <Modal show={show} onHide={handleModalHide} centered backdrop="static" className="teacher-merge-classes-modal">
      {stage === "form" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Merge Classes</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="merge-classes-intro">Use this when two versions of the same class were created and children or results need to come together.</p>
            {error && <Alert variant="danger">{error}</Alert>}
            {sameClass && <Alert variant="danger">Choose two different classes.</Alert>}

            <h3 className="merge-classes-question">Class to merge from</h3>
            <Form.Group className="merge-classes-section" controlId="mergeClassFrom">
              <Form.Label>Search class</Form.Label>
              <Form.Control
                type="search"
                aria-label="Class to merge from"
                value={fromClassName}
                onChange={(event) => {
                  setFromClassName(event.target.value);
                  setSelectedFromClass(null);
                }}
              />
              <Form.Text as="div">This class will be emptied after merge.</Form.Text>
            </Form.Group>
            <div className="merge-classes-results">
              {renderClassResults(
                filteredFromClasses,
                selectedFromClass,
                (classItem) => {
                  setSelectedFromClass(classItem);
                  setFromClassName(classItem?.name || "");
                },
                fromClassName.trim() ? "No matching source classes found." : "Search for the class to merge from."
              )}
            </div>

            <h3 className="merge-classes-question">Class to keep</h3>
            <Form.Group className="merge-classes-section" controlId="mergeClassTo">
              <Form.Label>Search class</Form.Label>
              <Form.Control
                type="search"
                aria-label="Class to keep"
                value={toClassName}
                onChange={(event) => {
                  setToClassName(event.target.value);
                  setSelectedToClass(null);
                }}
              />
              <Form.Text as="div">Children and results move into this class.</Form.Text>
            </Form.Group>
            <div className="merge-classes-results">
              {renderClassResults(
                filteredToClasses,
                selectedToClass,
                (classItem) => {
                  setSelectedToClass(classItem);
                  setToClassName(classItem?.name || "");
                },
                toClassName.trim() ? "No matching destination classes found." : "Search for the class to keep."
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <span className="merge-classes-footer-note">Merge review will check children and results.</span>
            <Button variant="outline-secondary" onClick={handleClose}>Cancel</Button>
            <Button variant="primary" onClick={loadDuplicateChildren} disabled={!canContinue || isLoadingDuplicates}>
              {isLoadingDuplicates ? "Checking..." : "Next"}
            </Button>
          </Modal.Footer>
        </>
      )}

      {stage === "review" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Review Merge</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <Alert variant="warning" className="merge-classes-warning">
              <strong>{duplicateChildren.length > 0 ? "Duplicate children found" : "No duplicate child names found"}</strong>
              <div>
                Children only in {selectedFromClassName} will move. Children already in both classes need matching. Results move with each child. The emptied class will be removed from your class list after merge.
              </div>
            </Alert>

            <h3 className="merge-classes-question">Merge summary</h3>
            <Table bordered responsive size="sm" className="merge-classes-summary">
              <tbody>
                <tr>
                  <th scope="row" className="w-25">Children moving</th>
                  <td>{sourceOnlyChildrenCount} only in source class</td>
                </tr>
                <tr>
                  <th scope="row">Need matching</th>
                  <td>{duplicateChildren.length > 0 ? duplicateChildren.map((duplicateChild) => duplicateChild.name).join(", ") : "None"}</td>
                </tr>
                <tr>
                  <th scope="row">Results</th>
                  <td>Results move with each matched child</td>
                </tr>
              </tbody>
            </Table>

            <Form.Group className="merge-classes-section merge-classes-owner-section" controlId="mergeClassOwnerTeacher">
              <Form.Label>Who should own {selectedToClassName}?</Form.Label>
              <Form.Control
                type="search"
                aria-label="Pick teacher to own class"
                value={ownerTeacherSearch}
                onChange={(event) => {
                  setOwnerTeacherSearch(event.target.value);
                  setSelectedOwnerTeacher(null);
                }}
              />
              <div className="merge-classes-results">
                {renderOwnerTeacherResults()}
              </div>
            </Form.Group>

            <Form.Group className="merge-classes-section">
              <Form.Label>What happens to {selectedFromClassName} after merge?</Form.Label>
              {SOURCE_CLASS_DISPOSITIONS.map((option) =>
                renderRadioCard(
                  {
                    ...option,
                    detail: option.detail(selectedFromClassName)
                  },
                  emptiedClassAction === option.value,
                  setEmptiedClassAction
                )
              )}
            </Form.Group>

            {hasNeedsReviewDecision && (
              <Alert variant="warning" className="merge-classes-warning">
                <strong>Support review needed</strong>
                <div>This merge cannot be submitted while any child is marked for matching help.</div>
              </Alert>
            )}

            {duplicateChildren.map((duplicateChild) => (
              <Form.Group className="merge-classes-section" key={duplicateChild.sourceStudentId}>
                <Form.Label>{duplicateChild.name} appears in both classes</Form.Label>
                {DUPLICATE_DECISIONS.map((option) =>
                  renderRadioCard(
                    option,
                    duplicateDecisions[duplicateChild.sourceStudentId] === option.value,
                    (value) => setDuplicateDecisions((currentDecisions) => ({
                      ...currentDecisions,
                      [duplicateChild.sourceStudentId]: value
                    }))
                  )
                )}
              </Form.Group>
            ))}
          </Modal.Body>
          <Modal.Footer>
            <span className="merge-classes-footer-note">
              {duplicateMatchingComplete ? "Ready to merge." : hasNeedsReviewDecision ? "Support review needed." : "Pick owner and merge decisions."}
            </span>
            <Button variant="outline-secondary" onClick={() => setStage("form")} disabled={isSubmittingMerge}>Back</Button>
            <Button variant="primary" onClick={handleSubmitMerge} disabled={!duplicateMatchingComplete || isSubmittingMerge}>
              {isSubmittingMerge ? "Merging..." : "Merge classes"}
            </Button>
          </Modal.Footer>
        </>
      )}

      {stage === "success" && (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Classes Merged</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="merge-classes-intro">The selected classes have been merged.</p>
            <Alert variant="success" className="merge-classes-success">
              <FaCheckCircle aria-hidden="true" />
              <div>
                <strong>{selectedFromClassName} merged into {selectedToClassName}</strong>
                <div>
                  Owner: {selectedOwnerTeacher?.name || "Not selected"}. Audit reference: {mergeResult?.auditReference || mergeResult?.mergeId || "Recorded"}.
                </div>
              </div>
            </Alert>
            <Table bordered responsive size="sm" className="merge-classes-summary">
              <tbody>
                <tr>
                  <th scope="row">Students moved</th>
                  <td>{mergeResult?.movedStudentCount ?? 0}</td>
                </tr>
                <tr>
                  <th scope="row">Duplicates combined</th>
                  <td>{mergeResult?.mergedDuplicateCount ?? 0}</td>
                </tr>
                <tr>
                  <th scope="row">Duplicates kept separate</th>
                  <td>{mergeResult?.keptSeparateDuplicateCount ?? 0}</td>
                </tr>
                <tr>
                  <th scope="row">Results transferred</th>
                  <td>{mergeResult?.transferredAssessmentCount ?? 0}</td>
                </tr>
                <tr>
                  <th scope="row">Source class</th>
                  <td>{mergeResult?.sourceClassFinalStatus || "Updated"}</td>
                </tr>
              </tbody>
            </Table>
            <div className="merge-classes-next">
              <strong>What next?</strong>
              <div>
                {embedded
                  ? "Back returns to the start of this workflow. Done returns to SENSAI."
                  : "Back returns to the start of this workflow. Return to Teacher Dashboard opens your class list."}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <span className="merge-classes-footer-note">Task complete.</span>
            <Button variant="outline-secondary" onClick={() => setStage("form")}>Back</Button>
            {!embedded && onReturnToTeacherDashboard && (
              <Button variant="outline-secondary" onClick={onReturnToTeacherDashboard}>Return to Teacher Dashboard</Button>
            )}
            <Button variant="primary" onClick={handleDone}>Done</Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
}

export default TeacherMergeClassesModal;
